import { AuctionStatus } from '@/types/LiveAuctionState';
import { BuyNowStatus } from '@/types/item/ItemSummary';
import {
    getCatalogIsTimed,
    getCatalogIsTimedPlus,
    getCatalogStatus,
} from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { getCatalogRegistration } from '@/redux/modules/catalog/registration/catalogRegistration.selectors';
import { getCountDown } from '@/redux/modules/bidCountDown';
import { getItemModelBuyNowStatus } from '@/redux/modules/item/summary/itemSummary.selectors';
import { useAppSelector } from '@/redux/hooks';
import React, { useEffect, useRef } from 'react';

// Initial countdown value - timer becomes visible when count < 5
export const INITIAL_COUNTDOWN_TIME = 21;

type Props = {
    catalogId: number;
    itemId: number;
    onCountdownEnd: () => void;
};

export const useGetShowCountdown = ({ catalogId, itemId, onCountdownEnd }: Props) => {
    const [count, setCount] = React.useState<number>(INITIAL_COUNTDOWN_TIME);
    const countRef = React.useRef<number>(count);
    countRef.current = count;
    const timer = useRef<ReturnType<typeof setInterval>>(null);
    const { approved } = useAppSelector((state) => getCatalogRegistration(state, catalogId));
    const isTimed = useAppSelector((state) => getCatalogIsTimed(state, catalogId));
    const buyNowStatus = useAppSelector((state) => getItemModelBuyNowStatus(state, itemId));
    const buyItNowAvailable = buyNowStatus === BuyNowStatus.Available;
    const isTimedPlus = useAppSelector((state) => getCatalogIsTimedPlus(state, catalogId));
    const timeStamp = useAppSelector((state) => getCountDown(state, itemId));
    const catalogStatus = useAppSelector((state) => getCatalogStatus(state, catalogId));

    const shouldShowTimer =
        Boolean(timeStamp) && isTimed && count < 5 && approved && catalogStatus === AuctionStatus.Live;

    useEffect(() => {
        if (!timer.current && Boolean(timeStamp) && !buyItNowAvailable && !isTimedPlus) {
            timer.current = setInterval(() => {
                if (countRef.current > 0) {
                    setCount(countRef.current - 1);
                    countRef.current = countRef.current - 1;
                } else if (countRef.current === 0) {
                    onCountdownEnd();
                }
            }, 1000);
        } else if (!Boolean(timeStamp)) {
            clearInterval(timer.current);
            timer.current = null;
        }
        return () => {
            clearInterval(timer.current);
            timer.current = null;
        };
    }, [onCountdownEnd, timeStamp, buyItNowAvailable, isTimedPlus]);

    useEffect(() => {
        setCount(timeStamp < 0 ? 4 : INITIAL_COUNTDOWN_TIME);
    }, [timeStamp]);

    return shouldShowTimer;
};
